import { useState, useRef, useCallback } from 'react';
import { useApi } from '../hooks/useApi';
import { useToast } from '../components/Toast';
import { api, extractErrorMessage, getAccessToken } from '../api/client';

interface ReferenciaInfo {
  existe: boolean;
  nombreArchivo?: string;
  fechaSubida?: string;
  totalFilas?: number;
}

const API_BASE = (import.meta as any).env?.VITE_API_BASE || '';

async function uploadAndDownload(endpoint: string, file: File): Promise<{ blob: Blob; filename: string }> {
  const formData = new FormData();
  formData.append('file', file);
  const headers: Record<string, string> = {};
  const token = getAccessToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${endpoint}`, {
    method: 'POST',
    body: formData,
    headers,
    credentials: 'include',
  });

  if (!res.ok) {
    const errData = await res.json().catch(() => null);
    throw { status: res.status, detail: errData?.message ?? `HTTP ${res.status}` };
  }

  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match?.[1] ?? 'precios-actualizados.xlsx';
  return { blob, filename };
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function ActualizacionPreciosPage() {
  const { data: refInfo, loading: loadingRef, refetch: refetchRef } = useApi<ReferenciaInfo>('/api/v3/precios/referencia-info');
  const { addToast: showToast } = useToast();

  const [uploadingRef, setUploadingRef] = useState(false);
  const [calculando, setCalculando] = useState(false);
  const [refFile, setRefFile] = useState<File | null>(null);
  const [mlFile, setMlFile] = useState<File | null>(null);

  const refInputRef = useRef<HTMLInputElement>(null);
  const mlInputRef = useRef<HTMLInputElement>(null);

  const handleSubirReferencia = useCallback(async () => {
    if (!refFile) return;
    setUploadingRef(true);
    try {
      const result = await api.upload<ReferenciaInfo>('/api/v3/precios/subir-referencia', refFile);
      showToast(`Referencia cargada: ${result.totalFilas} publicaciones`, 'success');
      refetchRef();
      setRefFile(null);
      if (refInputRef.current) refInputRef.current.value = '';
    } catch (err) {
      showToast(extractErrorMessage(err), 'error');
    } finally {
      setUploadingRef(false);
    }
  }, [refFile, refetchRef, showToast]);

  const handleCalcular = useCallback(async () => {
    if (!mlFile) return;
    setCalculando(true);
    try {
      const { blob, filename } = await uploadAndDownload('/api/v3/precios/calcular', mlFile);
      downloadBlob(blob, filename);
      showToast('Precios calculados. El archivo se descargó automáticamente.', 'success');
      setMlFile(null);
      if (mlInputRef.current) mlInputRef.current.value = '';
    } catch (err) {
      showToast(extractErrorMessage(err), 'error');
    } finally {
      setCalculando(false);
    }
  }, [mlFile, showToast]);

  const info = refInfo;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-800">Actualización Masiva de Precios</h1>
        <p className="mt-1 text-sm text-slate-500">
          Calculá nuevos precios para tus publicaciones de MercadoLibre en base al archivo de Formación de Precios.
        </p>
      </div>

      {/* Step 1: Upload reference */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-start gap-4">
          <div className="w-8 h-8 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center text-sm font-bold shrink-0">1</div>
          <div className="flex-1 space-y-3">
            <div>
              <h2 className="font-medium text-slate-800">Archivo de Formación de Precios</h2>
              <p className="text-sm text-slate-500 mt-0.5">
                Subí el archivo Excel de Formación de Precios (hoja <strong>Form-Precios</strong>). Solo necesitás hacerlo cuando actualizás listas de proveedores.
              </p>
            </div>

            {/* Current reference status */}
            {!loadingRef && (
              <div className={`rounded-lg px-4 py-3 text-sm flex items-center gap-2 ${
                info?.existe
                  ? 'bg-green-50 border border-green-200 text-green-800'
                  : 'bg-amber-50 border border-amber-200 text-amber-800'
              }`}>
                {info?.existe ? (
                  <>
                    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>
                      <strong>{info.nombreArchivo}</strong> — {info.totalFilas?.toLocaleString()} publicaciones —{' '}
                      subido el {new Date(info.fechaSubida!).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 3a9 9 0 110 18A9 9 0 0112 3z" />
                    </svg>
                    <span>No hay archivo de referencia cargado todavía.</span>
                  </>
                )}
              </div>
            )}

            <div className="flex items-center gap-3">
              <label className="cursor-pointer flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 bg-white text-sm text-slate-700 hover:bg-slate-50 transition-colors">
                <svg className="w-4 h-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                {refFile ? refFile.name : 'Seleccionar archivo XLSX…'}
                <input
                  ref={refInputRef}
                  type="file"
                  accept=".xlsx"
                  className="hidden"
                  onChange={e => setRefFile(e.target.files?.[0] ?? null)}
                />
              </label>
              <button
                onClick={handleSubirReferencia}
                disabled={!refFile || uploadingRef}
                className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {uploadingRef ? 'Subiendo…' : 'Cargar referencia'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Step 2: Calculate */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-start gap-4">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
            info?.existe ? 'bg-brand-100 text-brand-700' : 'bg-slate-100 text-slate-400'
          }`}>2</div>
          <div className="flex-1 space-y-3">
            <div>
              <h2 className="font-medium text-slate-800">Calcular precios</h2>
              <p className="text-sm text-slate-500 mt-0.5">
                Subí el Excel descargado desde MercadoLibre (el que tiene columnas ITEM_ID, VARIATION_ID, PRICE, etc.).
                El sistema calcula los precios nuevos y te devuelve el mismo archivo con la columna PRICE actualizada.
              </p>
            </div>

            {!info?.existe && (
              <p className="text-sm text-amber-600 italic">Primero cargá el archivo de referencia en el paso 1.</p>
            )}

            <div className="flex items-center gap-3">
              <label className={`cursor-pointer flex items-center gap-2 px-4 py-2 rounded-lg border text-sm transition-colors ${
                info?.existe
                  ? 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                  : 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed'
              }`}>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                {mlFile ? mlFile.name : 'Seleccionar Excel de ML…'}
                <input
                  ref={mlInputRef}
                  type="file"
                  accept=".xlsx"
                  className="hidden"
                  disabled={!info?.existe}
                  onChange={e => setMlFile(e.target.files?.[0] ?? null)}
                />
              </label>
              <button
                onClick={handleCalcular}
                disabled={!mlFile || calculando || !info?.existe}
                className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {calculando ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Calculando…
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 11h.01M12 11h.01M15 11h.01M4 19h16a2 2 0 002-2V7a2 2 0 00-2-2H4a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Calcular y descargar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* How it works */}
      <div className="bg-slate-50 rounded-xl border border-slate-200 p-5">
        <h3 className="text-sm font-medium text-slate-700 mb-3">¿Cómo funciona?</h3>
        <ol className="space-y-2 text-sm text-slate-600">
          <li className="flex gap-2">
            <span className="text-slate-400 shrink-0">1.</span>
            <span>El sistema lee la hoja <strong>Form-Precios</strong> del archivo de referencia y extrae el precio calculado (PRECIO PUBLICACION ML) por cada VARIATION_ID y MLA_ID.</span>
          </li>
          <li className="flex gap-2">
            <span className="text-slate-400 shrink-0">2.</span>
            <span>Para cada fila del Excel de ML, busca el precio correspondiente por VARIATION_ID (primero) o por ITEM_ID (si no tiene variante).</span>
          </li>
          <li className="flex gap-2">
            <span className="text-slate-400 shrink-0">3.</span>
            <span>Actualiza la columna PRICE y agrega una columna ESTADO_CALCULO con el resultado. Las filas sin precio en la referencia quedan marcadas como <em>SIN PRECIO EN REFERENCIA</em>.</span>
          </li>
          <li className="flex gap-2">
            <span className="text-slate-400 shrink-0">4.</span>
            <span>Descargás el Excel resultante y lo subís a MercadoLibre directamente.</span>
          </li>
        </ol>
      </div>
    </div>
  );
}
